import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Clock, MapPin, Zap, Map } from 'lucide-react';

const TripDetail = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [tripMeta, setTripMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetch('/itineraries/index.json').then((res) => res.json()),
      fetch(`/itineraries/generic-itineraries/${slug}.json`).then((res) => {
        if (!res.ok) throw new Error('Trip not found');
        return res.json();
      }),
    ])
      .then(([index, itinerary]) => {
        const meta = index.trips?.find((t) => t.slug === slug) || null;
        setTripMeta(meta);
        setData(itinerary);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [slug]);

  if (loading) {
    return (
      <main className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </main>
    );
  }

  if (error || !data) {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center px-5">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Trip not found</h1>
        <p className="text-gray-600 mb-8">We could not find the itinerary you are looking for.</p>
        <Link
          to="/trips"
          className="inline-flex items-center px-6 py-3 bg-primary text-white rounded-full font-medium transition-all hover:shadow-lg"
        >
          Back to all trips
        </Link>
      </main>
    );
  }

  const budgetToSymbol = {
    Budget: '$',
    Moderate: '$$',
    'Mid-range': '$$',
    Luxury: '$$$',
  };

  const { itinerary, cities, tags, budgetLevel, country, practicalInfo, sources } = data;

  return (
    <main className="bg-white">
      {/* Hero */}
      <section className="relative h-screen overflow-hidden flex items-end pb-16 pt-32">
        <div className="absolute inset-0">
          <img
            src={tripMeta?.coverImage || 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=1200'}
            alt={itinerary.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/10" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-5 w-full">
          <Link
            to="/trips"
            className="inline-flex items-center text-white/80 hover:text-white text-sm font-medium mb-6 transition-colors"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            All trips
          </Link>

          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="bg-white/20 backdrop-blur-sm text-white text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1.5">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {country}
            </span>
            <span className="bg-primary/90 text-white text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1.5">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {itinerary.durationDays} Days
            </span>
            <span className="bg-white/20 backdrop-blur-sm text-white text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1.5">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <span className="capitalize">{itinerary.pace}</span>
            </span>
            <span className="bg-white/20 backdrop-blur-sm text-white text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1.5">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {budgetToSymbol[budgetLevel] || budgetLevel}
            </span>
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
            {itinerary.title}
          </h1>

          <p className="text-lg text-white/90 max-w-3xl leading-relaxed mb-6">
            {itinerary.summary}
          </p>

          <div className="flex flex-wrap gap-2">
            {tags.map((tag) => (
              <span
                key={tag}
                className="text-xs bg-white/20 backdrop-blur-sm text-white px-2.5 py-1 rounded-full capitalize"
              >
                {tag.replace(/-/g, ' ')}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Route Overview */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-5">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">The Route</h2>
            <div className="w-24 h-1 bg-primary mx-auto rounded-full mb-4" />
          </div>

          <div className="flex flex-wrap items-center justify-center gap-8 text-lg text-gray-500 mb-4">
              <div className="flex items-center gap-2">
                <Clock className="w-6 h-6 text-primary" />
                <span>{itinerary.durationDays} days total</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-6 h-6 text-primary" />
                <span>{itinerary.route.length} cities</span>
              </div>
              <div className="flex items-center gap-2">
                <Zap className="w-6 h-6 text-primary" />
                <span className="capitalize">{itinerary.pace}</span>
              </div>
            </div>

          <div className="flex items-center justify-center gap-3 text-lg">
              <Map className="w-6 h-6 text-primary" />
              <span className="font-medium text-gray-700 whitespace-nowrap">Route:</span>
              <span className="text-gray-500"> {itinerary.route.join(' • ')}</span>
            </div>
        </div>
      </section>

      {/* Day-by-Day Timeline */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-5">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Itinerary</h2>
            <div className="w-24 h-1 bg-primary mx-auto rounded-full mb-4" />
          </div>

          <div className="space-y-12">
            {itinerary.days.map((day) => (
              <div key={day.dayNumber}>
                {/* Card */}
                <div className="relative bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                  {/* Day badge */}
                  <div className="absolute top-4 right-4 text-xs font-medium bg-primary text-white px-2.5 py-1 rounded-full">
                    Day {day.dayNumber}
                  </div>
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <span className="text-xs font-medium bg-primary/10 text-primary px-2.5 py-1 rounded-full">
                        {day.city}
                      </span>
                      <span className="text-xs font-medium bg-gray-100 text-gray-500 px-2.5 py-1 rounded-full capitalize">
                        {day.pace}
                      </span>
                      {day.transport && (
                        <span className="text-xs font-medium bg-blue-50 text-blue-600 px-2.5 py-1 rounded-full">
                          {day.transport.mode} to {day.transport.to}
                        </span>
                      )}
                      {day.dayTrip && (
                        <span className="text-xs font-medium bg-amber-50 text-amber-600 px-2.5 py-1 rounded-full">
                          Day trip
                        </span>
                      )}
                    </div>

                    <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">
                      {day.title}
                    </h3>

                    {/* Activities */}
                    <div className="space-y-3 mb-4">
                      {day.activities.map((activity) => (
                        <div
                          key={activity.order}
                          className="flex items-start gap-3 p-3 rounded-xl bg-gray-50/80"
                        >
                          <div className="mt-0.5">
                            <div
                              className={`w-2.5 h-2.5 rounded-full ${
                                activity.priority === 'must-see'
                                  ? 'bg-primary/70'
                                  : activity.priority === 'recommended'
                                  ? 'bg-primary/40'
                                  : 'bg-gray-300'
                              }`}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="font-semibold text-gray-900 text-sm md:text-base">
                                {activity.name}
                              </h4>
                              {activity.bookingRequired && (
                                <span className="text-[10px] font-medium bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                                  Book ahead
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                              <span>{activity.type}</span>
                              <span>•</span>
                              <span>{activity.duration}</span>
                            </div>
                            <p className="text-sm text-gray-600 mt-1 leading-relaxed">
                              {activity.description}
                            </p>
                            {activity.notes && (
                              <div className="mt-2 flex items-start gap-2 bg-teal-50 border-l-4 border-teal-500 rounded-r-lg p-2.5">
                                <svg className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <p className="text-xs text-teal-800 font-medium leading-relaxed">
                                  {activity.notes}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Tips */}
                    {day.tips && day.tips.length > 0 && (
                      <div className="bg-amber-50/60 border border-amber-100 rounded-xl p-4">
                        <div className="flex items-start gap-2">
                          <svg className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <p className="text-sm text-amber-800 leading-relaxed">
                            {day.tips[0]}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
        </div>
      </section>

      {/* Cities Deep Dive */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-5">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">The Cities</h2>
            <div className="w-24 h-1 bg-primary mx-auto rounded-full mb-4" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {cities.map((city) => (
              <div
                key={city.name}
                className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-gray-100"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-900">{city.name}</h3>
                  <span className="text-xs font-medium bg-primary/10 text-primary px-2.5 py-1 rounded-full capitalize">
                    {city.role.replace(/-/g, ' ')}
                  </span>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  {city.description}
                </p>
                <div className="space-y-2 text-sm text-gray-500">
                  <div className="flex items-start gap-2">
                    <svg className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{city.recommendedDurationDays} day{city.recommendedDurationDays > 1 ? 's' : ''} recommended</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <svg className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span className="text-sm">{city.recommendedStayArea}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Practical Info */}
      {practicalInfo && (
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-5">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Practical Information</h2>
              <div className="w-24 h-1 bg-primary mx-auto rounded-full mb-4" />
            </div>

            <div className="bg-white rounded-2xl p-6 md:p-10 shadow-lg border border-gray-100">
              {practicalInfo.visaRequirements && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                    Visa
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{practicalInfo.visaRequirements}</p>
                </div>
              )}

              {practicalInfo.gettingAround && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                    </svg>
                    Getting Around
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{practicalInfo.gettingAround}</p>
                </div>
              )}

              {practicalInfo.safetyTips && practicalInfo.safetyTips.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    Safety Tips
                  </h3>
                  <ul className="list-disc list-outside pl-5 space-y-2">
                    {practicalInfo.safetyTips.map((tip, i) => (
                      <li key={i} className="text-gray-600 text-sm leading-relaxed">{tip}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Sources */}
      {sources && sources.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-5">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Sources</h2>
              <div className="w-24 h-1 bg-primary mx-auto rounded-full mb-4" />
              <p className="text-gray-600 text-sm">
                Information curated from official tourism boards and operators.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {sources.map((source, i) => (
                <a
                  key={i}
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-start gap-4 bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300"
                >
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                    <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </div>
                  <div className="min-w-0">
                    <h4 className="font-medium text-gray-900 text-sm truncate">{source.title}</h4>
                    <p className="text-xs text-gray-400 capitalize">{source.type.replace(/-/g, ' ')}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="relative py-24 bg-gradient-to-br from-primary via-primary to-blue-600 text-white text-center overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-80 h-80 bg-white/10 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-5">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
            Start Your {itinerary.route[0]} Adventure
          </h2>
          <p className="text-xl opacity-90 leading-relaxed max-w-2xl mx-auto mb-10">
            Download TravelMaker and explore every stop with audio guides, maps, and local tips in your pocket.
          </p>

          <div className="flex gap-6 justify-center md:flex-row flex-col items-center">
            <a
              href="https://apps.apple.com/app/travelmaker-ai/id6742571605"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block transition-all duration-300 ease-in-out hover:-translate-y-1 hover:shadow-2xl"
            >
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg"
                alt="Download on the App Store"
                className="h-15 w-44 object-contain block"
              />
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.comrades.travelmaker"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block transition-all duration-300 ease-in-out hover:-translate-y-1 hover:shadow-2xl"
            >
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                alt="Get it on Google Play"
                className="h-15 w-44 object-contain block"
              />
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default TripDetail;
